import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GithubMark, XMark } from "../BrandIcons";
import { fetchPublicProfile, shortAddr, type PublicIdentityProfile } from "../../lib/api";
import {
  fetchPublicX402ProfileByWallet,
  toBaseAccountId,
  type X402ProfileActivity,
  type X402PublicProfileResponse,
} from "../../lib/x402Profile";
import type { X402LinkedAccount, X402Route } from "../../lib/x402Discovery";

interface SocialSignal {
  routeId: string;
  title: string;
  kind: "recognized" | "reviewed" | "published";
  reviewText: string | null;
}

interface SocialParticipant {
  wallet: string;
  accounts: X402LinkedAccount[];
  signals: SocialSignal[];
}

interface LoadedParticipant {
  identity: PublicIdentityProfile | null;
  walletProfile: X402PublicProfileResponse | null;
}

export function X402SocialTrail({ routes }: { routes: X402Route[] }) {
  const participants = useMemo(() => collectParticipants(routes), [routes]);
  const routeTitles = useMemo(() => new Map(routes.map((route) => [route.routeId, routeTitle(route)])), [routes]);
  const [loaded, setLoaded] = useState<Record<string, LoadedParticipant>>({});
  const participantKey = participants.map((participant) => participant.wallet.toLowerCase()).join(":");

  useEffect(() => {
    if (participants.length === 0) {
      setLoaded({});
      return;
    }
    let cancelled = false;
    Promise.all(participants.map(async (participant) => {
      const primary = preferredAccount(participant.accounts);
      const [identityResult, walletProfileResult] = await Promise.allSettled([
        primary ? fetchPublicProfile(primary.handle) : Promise.resolve(null),
        fetchPublicX402ProfileByWallet(toBaseAccountId(participant.wallet)),
      ]);
      return [participant.wallet.toLowerCase(), {
        identity: identityResult.status === "fulfilled" && identityResult.value && "profile" in identityResult.value
          ? identityResult.value.profile
          : null,
        walletProfile: walletProfileResult.status === "fulfilled" ? walletProfileResult.value : null,
      }] as const;
    })).then((entries) => {
      if (!cancelled) setLoaded(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [participantKey]);

  if (participants.length === 0) return null;

  return (
    <div className={`grid gap-4 ${participants.length === 1 ? "md:grid-cols-[minmax(0,1.35fr)_minmax(16rem,0.65fr)]" : "md:grid-cols-2 xl:grid-cols-3"}`}>
      {participants.map((participant) => {
        const primary = preferredAccount(participant.accounts);
        const data = loaded[participant.wallet.toLowerCase()];
        const publicUse = data?.walletProfile?.activity?.find((fact) => fact.kind === "used" || fact.kind === "paid_settlement") ?? null;
        const signal = participant.signals[0];
        const action = publicUse ? publicUseLabel(publicUse, routeTitles) : signal ? `${signalVerb(signal.kind)} ${signal.title}` : "Published x402 activity";
        const verificationLabel = publicUse ? "verified use" : signal?.kind === "published" ? "signed route note" : "review";
        const displayName = data?.identity?.displayName || primary?.handle.replace(/^[^:]+:/, "") || shortAddr(participant.wallet);
        const profileHref = primary
          ? `/p/${encodeURIComponent(primary.handle)}`
          : `/x402/reviewers/${participant.wallet}`;
        return (
          <Link
            key={participant.wallet}
            to={profileHref}
            aria-label={`Open ${displayName}'s Boon profile`}
            className="group block min-w-0 overflow-hidden rounded-xl border border-faint bg-paper p-5 transition-[background-color,border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-olive hover:bg-paper-deep/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive focus-visible:ring-offset-2"
          >
            <div className="flex items-start gap-3">
              {data?.identity?.avatarUrl ? (
                <img src={data.identity.avatarUrl} alt="" className="h-12 w-12 shrink-0 rounded-full border border-faint object-cover" />
              ) : (
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-olive/35 bg-olive-soft text-olive-deep" aria-hidden="true">
                  <PersonGlyph className="h-6 w-6" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="min-w-0 break-words [overflow-wrap:anywhere] text-lg font-display tracking-tight text-ink group-hover:text-olive-deep group-hover:underline underline-offset-4">{displayName}</h3>
                  <span className="flex items-center gap-1" aria-label="Linked public identities">
                    {participant.accounts.map((account) => <ProviderBadge key={account.handle} account={account} />)}
                  </span>
                </div>
                <p className="mt-0.5 max-w-full truncate btn-mono text-xs text-muted" title={primary?.handle ?? participant.wallet}>{primary?.handle ?? shortAddr(participant.wallet)}</p>
              </div>
              <span className={`pill shrink-0 btn-mono text-[10px] ${publicUse ? "pill-olive" : "pill-faint"}`}>{verificationLabel}</span>
            </div>

            <div className="mt-5 min-w-0">
              <p className="min-w-0 break-words [overflow-wrap:anywhere] text-base font-semibold text-ink leading-snug group-hover:text-olive-deep">{action}</p>
              {signal?.reviewText && <p className="mt-2 line-clamp-2 break-words [overflow-wrap:anywhere] text-sm text-muted leading-relaxed">“{signal.reviewText}”</p>}
            </div>
          </Link>
        );
      })}
      {participants.length === 1 && <SocialGuide />}
    </div>
  );
}

function SocialGuide() {
  const steps = [
    { label: "Pick a person", Icon: PersonGlyph, tone: "bg-clay-soft text-clay-deep" },
    { label: "See what they tried", Icon: CursorGlyph, tone: "bg-amber-soft text-amber-deep" },
    { label: "Open the route", Icon: RouteGlyph, tone: "bg-olive-soft text-olive-deep" },
  ] as const;
  return (
    <aside className="rounded-xl border border-faint bg-paper-deep/35 p-5" aria-label="How to use the social trail">
      <p className="text-sm font-semibold text-ink">Follow the public trail</p>
      <ol className="mt-4 space-y-3">
        {steps.map(({ label, Icon, tone }) => (
          <li key={label} className="flex items-center gap-3 text-sm text-muted">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone}`} aria-hidden="true"><Icon className="h-4 w-4" /></span>
            {label}
          </li>
        ))}
      </ol>
    </aside>
  );
}

function collectParticipants(routes: X402Route[]): SocialParticipant[] {
  const participants = new Map<string, SocialParticipant>();
  function add(wallet: string, accounts: X402LinkedAccount[], signal: SocialSignal) {
    if (accounts.length === 0) return;
    const key = wallet.toLowerCase();
    const current = participants.get(key) ?? { wallet, accounts: [], signals: [] };
    const knownAccounts = new Set(current.accounts.map((account) => account.handle));
    for (const account of accounts) if (!knownAccounts.has(account.handle)) current.accounts.push(account);
    if (!current.signals.some((entry) => entry.routeId === signal.routeId && entry.kind === signal.kind)) current.signals.push(signal);
    participants.set(key, current);
  }
  for (const route of routes) {
    const title = routeTitle(route);
    for (const review of route.reviews) {
      add(review.reviewer.wallet, review.reviewer.accounts, {
        routeId: route.routeId,
        title,
        kind: review.evidenceKind === "boon_backed" ? "recognized" : "reviewed",
        reviewText: review.reviewText,
      });
    }
    for (const context of route.contexts) {
      add(context.publisher, context.publisherAccounts, {
        routeId: route.routeId,
        title,
        kind: "published",
        reviewText: null,
      });
    }
  }
  return [...participants.values()]
    .sort((left, right) => {
      const leftProvider = preferredAccount(left.accounts)?.provider ?? "github";
      const rightProvider = preferredAccount(right.accounts)?.provider ?? "github";
      return providerRank(leftProvider) - providerRank(rightProvider) || right.signals.length - left.signals.length;
    })
    .slice(0, 3);
}

function preferredAccount(accounts: X402LinkedAccount[]): X402LinkedAccount | undefined {
  return [...accounts].sort((left, right) => providerRank(left.provider) - providerRank(right.provider))[0];
}

function providerRank(provider: X402LinkedAccount["provider"]): number {
  if (provider === "x") return 0;
  if (provider === "agent") return 1;
  return 2;
}

function routeTitle(route: X402Route): string {
  if (route.description?.summary) return route.description.summary;
  if (route.contexts[0]?.note.intent) return route.contexts[0].note.intent;
  const endpoint = route.endpointUrl?.publicEndpointUrl;
  if (!endpoint) return "this x402 route";
  try {
    const url = new URL(endpoint);
    return `${url.host}${url.pathname}`.replace(/\/$/, "");
  } catch {
    return endpoint;
  }
}

function publicUseLabel(fact: X402ProfileActivity, routeTitles: Map<string, string>): string {
  const title = fact.routeId ? routeTitles.get(fact.routeId) : null;
  if (title) return `Used ${title}`;
  const endpoint = `${fact.origin ?? ""}${fact.path ?? ""}`;
  return endpoint ? `Used ${fact.method ? `${fact.method} ` : ""}${endpoint}` : "Published a recent x402 use";
}

function signalVerb(kind: SocialSignal["kind"]): string {
  if (kind === "recognized") return "Recognized";
  if (kind === "published") return "Published notes for";
  return "Reviewed";
}

function ProviderBadge({ account }: { account: X402LinkedAccount }) {
  const className = "flex h-6 w-6 items-center justify-center rounded-full border border-faint bg-paper text-muted";
  return (
    <span className={className} title={account.handle}>
      {account.provider === "x" ? <XMark className="h-3 w-3" /> : account.provider === "github" ? <GithubMark className="h-3.5 w-3.5" /> : <RobotGlyph className="h-3.5 w-3.5" />}
      <span className="sr-only">{account.handle}</span>
    </span>
  );
}

type GlyphProps = { className?: string };
function PersonGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.25"/><path d="M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6"/></svg>; }
function RobotGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3v3M8 3h8M5 9h14v10H5z"/><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1" fill="currentColor" stroke="none"/><path d="M9 17h6"/></svg>; }
function CursorGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m5 3 12 9-6 1.5L8.5 19z"/><path d="m12 14 4 5"/></svg>; }
function RouteGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="6" cy="18" r="2"/><circle cx="18" cy="6" r="2"/><path d="M8 18h3a3 3 0 0 0 3-3v-6a3 3 0 0 1 3-3"/></svg>; }
